/**
 * Client-side error reporting — always logs to console; forwards to Sentry when
 * VITE_SENTRY_DSN is configured (see lib/sentry.ts).
 */

import { FirebaseError } from "firebase/app";
import { Sentry } from "./sentry";

export interface ErrorContext {
  weddingId?: string;
  guestId?: string;
  [key: string]: unknown;
}

/** Map Firebase error codes to user-facing Polish messages. */
export function firebaseErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof FirebaseError) {
    switch (err.code) {
      case "permission-denied":
        return "Brak uprawnień do zapisu. Skontaktuj się z administratorem lub spróbuj ponownie później.";
      case "unavailable":
        return "Usługa chwilowo niedostępna. Sprawdź połączenie i spróbuj ponownie.";
      case "unauthenticated":
        return "Sesja wygasła. Zaloguj się ponownie.";
      default:
        return fallback;
    }
  }
  if (err instanceof Error && err.message) {
    return fallback;
  }
  return fallback;
}

export function reportError(scope: string, err: unknown, context?: ErrorContext): void {
  const prefix = `[${scope}]`;
  console.error(prefix, err, context ?? {});

  if (import.meta.env.VITE_SENTRY_DSN) {
    Sentry.captureException(err, {
      tags: { scope },
      extra: context,
    });
  }
}

function isBenignRejection(reason: unknown): boolean {
  if (reason instanceof DOMException && reason.name === "AbortError") return true;
  if (reason instanceof Error && reason.name === "AbortError") return true;
  if (typeof reason === "object" && reason !== null && "code" in reason) {
    const code = (reason as { code: unknown }).code;
    if (code === "cancelled" || code === "canceled") return true;
  }
  return false;
}

let globalHandlersInstalled = false;

/** Report unhandled promise rejections that are not benign cancellations. */
export function installGlobalErrorHandlers(): void {
  if (globalHandlersInstalled || typeof window === "undefined") return;
  globalHandlersInstalled = true;

  window.addEventListener("unhandledrejection", (event) => {
    if (isBenignRejection(event.reason)) return;
    reportError("unhandledrejection", event.reason);
  });
}
